import axiosInstance from '../axios'
import type { SelectedMaterial } from '../types/calculadoraDeMateriales.ts'
import {
  documentationPipelinePhaseForLoader,
  extractProjectAutofillDraftIdFromResponse,
  parseDocumentationPipelinePayload,
  type DocumentationPipelinePollUpdate,
  type DocumentationPipelineStatus
} from './projectAutofillDraftService.ts'

const getAuthHeaders = () => {
  const authToken = localStorage.getItem('access-Token-Edify')
  return { headers: { Authorization: `Bearer ${authToken}` } }
}

const DOCUMENTATION_PIPELINE_POLL_MAX_MS = 600000
const DOCUMENTATION_PIPELINE_POLL_INTERVAL_MS = 2000

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

export async function getMaterialsQuotationDocumentationPipeline(
  draftId: string
): Promise<DocumentationPipelineStatus> {
  const response = await axiosInstance.get(
    `/materials-quotation-autofill/draft/${draftId}/documentation-pipeline`,
    getAuthHeaders()
  )
  return parseDocumentationPipelinePayload(response.data)
}

function pipelineError(
  code: string,
  message: string,
  userMessage?: string
): Error & { code?: string; userMessage?: string } {
  const e = new Error(message) as Error & { code?: string; userMessage?: string }
  e.code = code
  e.userMessage = userMessage ?? message
  return e
}

export async function waitForMaterialsQuotationDocumentationPipeline(
  draftId: string,
  onUpdate: (update: DocumentationPipelinePollUpdate) => void,
  options?: { maxWaitMs?: number; intervalMs?: number; signal?: AbortSignal }
): Promise<DocumentationPipelineStatus> {
  const maxWaitMs = options?.maxWaitMs ?? DOCUMENTATION_PIPELINE_POLL_MAX_MS
  const intervalMs = options?.intervalMs ?? DOCUMENTATION_PIPELINE_POLL_INTERVAL_MS
  const deadline = Date.now() + maxWaitMs
  const signal = options?.signal

  while (Date.now() < deadline) {
    if (signal?.aborted) {
      const err = new Error('Aborted') as Error & { name?: string }
      err.name = 'AbortError'
      throw err
    }
    const status = await getMaterialsQuotationDocumentationPipeline(draftId)
    const { phase, errorMessage } = status
    onUpdate({
      loaderPhase: documentationPipelinePhaseForLoader(status),
      status
    })
    if (phase === 'ready') {
      return status
    }
    if (phase === 'failed') {
      const msg =
        errorMessage && errorMessage.length > 0
          ? errorMessage
          : 'El análisis del documento falló.'
      throw pipelineError('DOCUMENTATION_PIPELINE_FAILED', msg, msg)
    }
    await delay(intervalMs)
  }
  throw pipelineError(
    'STREAM_TIMEOUT',
    'STREAM_TIMEOUT',
    'El análisis del documento tardó demasiado. Intentá de nuevo en unos minutos.'
  )
}

export type PostMaterialsQuotationAutofillFromDocumentResult =
  | { mode: 'async'; draftId: string; body: unknown }
  | { mode: 'sync'; body: unknown }

export async function postMaterialsQuotationAutofillDraftFromDocument(
  file: File
): Promise<PostMaterialsQuotationAutofillFromDocumentResult> {
  const fd = new FormData()
  fd.append('file', file)
  const response = await axiosInstance.post(
    '/materials-quotation-autofill/draft/from-document',
    fd,
    {
      ...getAuthHeaders()
    }
  )
  const raw = response.data
  const draftId = extractProjectAutofillDraftIdFromResponse(raw)
  if (response.status === 202) {
    return { mode: 'async', draftId, body: raw }
  }
  if (draftId) {
    return { mode: 'async', draftId, body: raw }
  }
  return { mode: 'sync', body: raw }
}

export type MaterialsQuotationDocumentRelevance =
  | 'materials_list_or_budget'
  | 'other_or_unclear'
  | 'unlikely_materials_list'

export type MaterialsAutofillAnalysisContext = {
  summary?: string
  documentRelevance?: MaterialsQuotationDocumentRelevance | null
  notesForUser?: string
}

const DOCUMENT_RELEVANCE_VALUES = new Set<string>([
  'materials_list_or_budget',
  'other_or_unclear',
  'unlikely_materials_list'
])

function parseMaterialsAutofillAnalysisContext(
  metadata: Record<string, unknown>
): MaterialsAutofillAnalysisContext | undefined {
  const raw = metadata.analysisContext
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined
  }
  const o = raw as Record<string, unknown>
  const summary = typeof o.summary === 'string' && o.summary.trim() ? o.summary.trim() : undefined
  const notesForUser =
    typeof o.notesForUser === 'string' && o.notesForUser.trim() ? o.notesForUser.trim() : undefined
  const dr = o.documentRelevance
  let documentRelevance: MaterialsQuotationDocumentRelevance | null | undefined
  if (dr === null) {
    documentRelevance = null
  } else if (typeof dr === 'string' && DOCUMENT_RELEVANCE_VALUES.has(dr)) {
    documentRelevance = dr as MaterialsQuotationDocumentRelevance
  }
  const out: MaterialsAutofillAnalysisContext = {}
  if (summary) out.summary = summary
  if (notesForUser) out.notesForUser = notesForUser
  if (documentRelevance !== undefined) out.documentRelevance = documentRelevance
  if (Object.keys(out).length === 0) {
    return undefined
  }
  return out
}

export function extractMaterialsAutofillDraftPayload(raw: unknown): {
  quotation?: Record<string, unknown>
  materialLines?: unknown[]
  analysisContext?: MaterialsAutofillAnalysisContext
} {
  if (!raw || typeof raw !== 'object') {
    return {}
  }
  const r = raw as Record<string, unknown>
  const data = r.data && typeof r.data === 'object' ? (r.data as Record<string, unknown>) : r
  const payload =
    data.payload && typeof data.payload === 'object' ? (data.payload as Record<string, unknown>) : data
  const quotation =
    payload.quotation && typeof payload.quotation === 'object'
      ? (payload.quotation as Record<string, unknown>)
      : undefined
  const materialLines = Array.isArray(payload.materialLines) ? payload.materialLines : undefined
  const metadata =
    payload.metadata && typeof payload.metadata === 'object' && !Array.isArray(payload.metadata)
      ? (payload.metadata as Record<string, unknown>)
      : undefined
  const analysisContext = metadata ? parseMaterialsAutofillAnalysisContext(metadata) : undefined
  return { quotation, materialLines, analysisContext }
}

export async function getMaterialsQuotationAutofillDraft(draftId: string): Promise<unknown> {
  const response = await axiosInstance.get(
    `/materials-quotation-autofill/draft/${draftId}`,
    getAuthHeaders()
  )
  return response.data
}

export async function putMaterialsQuotationAutofillDraft(
  draftId: string,
  body: { quotation?: Record<string, unknown>; materialLines?: unknown[] }
): Promise<unknown> {
  const response = await axiosInstance.put(
    `/materials-quotation-autofill/draft/${draftId}`,
    body,
    getAuthHeaders()
  )
  return response.data
}

export async function publishMaterialsQuotationAutofillDraft(
  draftId: string,
  options?: { fromSavedDraft?: boolean }
): Promise<unknown> {
  const q = options?.fromSavedDraft === true ? '?fromSavedDraft=true' : ''
  const response = await axiosInstance.post(
    `/materials-quotation-autofill/draft/${draftId}/publish${q}`,
    null,
    getAuthHeaders()
  )
  return response.data
}

export function quoteFormToAutofillQuotationPatch(quoteData: {
  name: string
  city: string
  receiveFrom: string
  receiveTo: string
  currency: string
  description: string
  jurisdiccion: string
  delivery: boolean
  latitude?: number | null
  longitude?: number | null
}): Record<string, unknown> {
  const patch: Record<string, unknown> = {
    name: quoteData.name,
    description: quoteData.description,
    zone: quoteData.city,
    startDate: quoteData.receiveFrom,
    endDate: quoteData.receiveTo,
    currency: quoteData.currency,
    delivery: quoteData.delivery === true
  }
  const lat = quoteData.latitude
  const lng = quoteData.longitude
  const hasCoords =
    typeof lat === 'number' &&
    Number.isFinite(lat) &&
    typeof lng === 'number' &&
    Number.isFinite(lng)
  if (hasCoords) {
    patch.latitude = lat
    patch.longitude = lng
  }
  if (quoteData.delivery === true) {
    if (hasCoords) {
      patch.deliveryNeed = {
        scope: 'LOCAL',
        latitude: lat,
        longitude: lng
      }
    } else {
      patch.deliveryNeed = {
        scope: 'NATIONWIDE'
      }
    }
  } else {
    patch.deliveryNeed = {
      scope: 'NATIONWIDE'
    }
  }
  if (quoteData.jurisdiccion) {
    const j = Number(quoteData.jurisdiccion)
    if (!Number.isNaN(j)) {
      patch.jurisdiccion = j
    }
  }
  return patch
}

export function selectedMaterialsToAutofillMaterialLines(
  materials: Array<{
    id: number | string
    name: string
    unit: string
    quantity: number
    category: string
    temporary?: boolean
    categoryId?: number | string
  }>,
  categories: Array<{ id: number | string; name: string }>
): unknown[] {
  const lines: unknown[] = []
  for (const m of materials) {
    if (m.temporary) {
      const categoryId = m.categoryId ?? categories.find((c) => c.name === m.category)?.id
      if (categoryId == null) continue
      const cid = Number(categoryId)
      if (Number.isNaN(cid)) continue
      lines.push({
        temporary: true,
        categoryId: cid,
        rawName: m.name,
        catalogUnit: m.unit,
        amount: m.quantity
      })
      continue
    }
    const sid = String(m.id)
    if (sid.startsWith('autofill-temp')) continue
    const mid = typeof m.id === 'number' ? m.id : Number.parseInt(sid, 10)
    if (!Number.isFinite(mid)) continue
    lines.push({
      materialId: mid,
      temporary: false,
      amount: m.quantity
    })
  }
  return lines
}

export type AutofillMaterialLineRecord = {
  materialId?: number | string | null
  temporary?: boolean
  categoryId?: number | string | null
  rawName?: string | null
  name?: string | null
  catalogUnit?: string | null
  unit?: string | null
  amount?: number | string | null
  quantity?: number | string | null
  inCatalog?: boolean
  catalogReferencePrice?: number | null
  catalogMaterialName?: string | null
  catalogMaterialUnquoted?: boolean
}

function resolveAutofillLineInCatalog(line: AutofillMaterialLineRecord): boolean {
  if (typeof line.inCatalog === 'boolean') return line.inCatalog
  if (line.temporary === true) return false
  return line.materialId != null && Number(line.materialId) > 0
}

export function materialAutofillLinesToSelectedMaterials(
  lines: unknown[],
  categories: Array<{
    id: number | string
    name: string
    materials?: Array<{ id: number | string; name: string; unit?: string; price?: number }>
  }>
): SelectedMaterial[] {
  const out: SelectedMaterial[] = []
  let tempCounter = 0
  for (const raw of lines) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue
    const line = raw as AutofillMaterialLineRecord
    const amountRaw = line.amount ?? line.quantity
    const quantity =
      typeof amountRaw === 'number' && Number.isFinite(amountRaw)
        ? Math.max(1, Math.round(amountRaw))
        : typeof amountRaw === 'string' && amountRaw.trim()
          ? Math.max(1, Math.round(Number.parseFloat(amountRaw)) || 1)
          : 1
    const unitRaw = (line.catalogUnit ?? line.unit ?? 'u') as string
    const unit = typeof unitRaw === 'string' && unitRaw.trim() ? unitRaw.trim() : 'u'
    const rawLabel =
      (typeof line.rawName === 'string' && line.rawName.trim()) ||
      (typeof line.name === 'string' && line.name.trim()) ||
      'Material'
    const rawNameStored = typeof line.rawName === 'string' && line.rawName.trim() ? line.rawName.trim() : undefined

    const inCatalog = resolveAutofillLineInCatalog(line)
    if (!inCatalog) {
      const cid = line.categoryId
      const cat = categories.find((c) => String(c.id) === String(cid))
      tempCounter += 1
      out.push({
        id: `autofill-temp-${tempCounter}`,
        name: rawLabel,
        unit,
        quantity,
        category: cat?.name ?? 'Sin Categoría',
        temporary: true,
        categoryId: cid ?? undefined,
        rawName: rawNameStored,
        inCatalog: false,
      })
      continue
    }

    const mid = line.materialId
    if (mid == null || mid === '') continue

    let foundName = ''
    let foundUnit = unit
    let foundCategory = 'Sin Categoría'
    let categoryId: number | string | undefined
    let catalogLookupPrice: number | undefined
    for (const c of categories) {
      const m = c.materials?.find((x) => String(x.id) === String(mid))
      if (m) {
        foundName = m.name
        foundUnit = m.unit ?? foundUnit
        foundCategory = c.name
        categoryId = c.id
        if (m.price != null && Number.isFinite(Number(m.price))) {
          catalogLookupPrice = Number(m.price)
        }
        break
      }
    }

    const catalogTitle =
      typeof line.catalogMaterialName === 'string' && line.catalogMaterialName.trim()
        ? line.catalogMaterialName.trim()
        : foundName
    const displayName = catalogTitle || `Material ${String(mid)}`

    let price: number | undefined
    if (line.catalogReferencePrice != null && Number.isFinite(Number(line.catalogReferencePrice))) {
      price = Number(line.catalogReferencePrice)
    } else if (catalogLookupPrice != null) {
      price = catalogLookupPrice
    }

    const row: SelectedMaterial = {
      id: mid,
      name: displayName,
      unit: foundUnit,
      quantity,
      category: foundCategory,
      temporary: false,
      categoryId,
      rawName: rawNameStored,
      inCatalog: true,
      catalogMaterialUnquoted: line.catalogMaterialUnquoted === true,
    }
    if (price != null) {
      row.price = price
    }
    out.push(row)
  }
  return out
}
